import type React from "react";
import { useState, useEffect, useMemo } from "react";
import { Tabs, Tab, Box, CircularProgress, Alert } from "@mui/material";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DashboardData } from "./types/dashboard";
import apiService from "../../service/Api";
import { logout } from "../../store/slice/AuthSlice";
import AdminDashboard from "./components/admin-widgets/AdminDashboard";
import SalesDashboard from "./components/sales-widgets/SalesDashboard";
import OperationsDashboard from "./components/operations-widgets/OperationsDashboard";
import CeoDashboard from "./components/ceo-widgets/CeoDashboard";
import VenuesDashboard from "./components/venues-widgets/VenuesDashboard";
import PixelPilotsDashboard from "./components/pixel-pilots/PixelPilotsDashboard";
import ChemaDashboard from "./components/chema-widgets/ChemaDashboard";
import FuneralHomesDashboard from "./components/funeral-homes/FuneralHomesDashboard";
import InsuranceCompaniesDashboard from "./components/insurance-companies/InsuranceCompaniesDashboard";
import AcademyDashboard from "./components/academy-widgets/AcademyDashboard";
import MyDashboardDashboard from "./components/my-dashboard-widgets/MyDashboard";
import MyBalanceDashboard from "./components/my-balance-widgets/MyBalanceDashboard";
import ContactsDashboard from "./components/contacts-widgets/ContactsDashboard";
import UserLoginMonitor from "./components/user-login-monitor/UserloginInfo";
import StreamsDashboard from "./components/stream-dashboard/StreamDashboard";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/permissions";
import NewFeatureBadge from "../stream-management/components/NewFeature";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

interface TabDef {
  id: string;
  label: string;
  permission?: string;
}

const TAB_DEFS: TabDef[] = [
  { id: "admin", label: "Admin", permission: PERMISSIONS.USER_MANAGEMENT_VIEW },
  { id: "my-dash", label: "My Dashboard" },
  { id: "sales", label: "Sales", permission: PERMISSIONS.BOOKING_VIEW },
  {
    id: "operations",
    label: "Operations",
    permission: PERMISSIONS.STREAM_VIEW,
  },
  { id: "streams", label: "Streams", permission: PERMISSIONS.STREAM_VIEW },
  { id: "ceo", label: "CEO", permission: PERMISSIONS.USER_MANAGEMENT_VIEW },
  { id: "contacts", label: "Contacts", permission: PERMISSIONS.CONTACT_VIEW },
  { id: "venues", label: "Venues", permission: PERMISSIONS.VENUE_VIEW },
  {
    id: "pilots",
    label: "Pixel Pilots",
    permission: PERMISSIONS.PIXEL_PILOT_VIEW,
  },
  {
    id: "user-login",
    label: "UserLogin",
    permission: PERMISSIONS.USER_MANAGEMENT_VIEW,
  },
  { id: "academy", label: "Academy", permission: PERMISSIONS.ACADEMY_VIEW },
  { id: "chema", label: "CHEMA", permission: PERMISSIONS.DONATION_VIEW },
  {
    id: "funeral",
    label: "Funeral Homes",
    permission: PERMISSIONS.BOOKING_VIEW,
  },
  {
    id: "insurance",
    label: "Insurance Companies",
    permission: PERMISSIONS.COMPANY_VIEW,
  },
  { id: "balance", label: "My Balance" },
];

const MainDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const tabParam = new URLSearchParams(location.search).get("tab");
    if (!tabParam) return;

    const tabIndex = TAB_DEFS.findIndex((tab) => tab.id === tabParam);
    if (tabIndex >= 0) {
      setTabValue(tabIndex);
    }
  }, [location.search]);

  const visibleTabs = useMemo(
    () =>
      TAB_DEFS.filter(
        (tab) => !tab.permission || hasPermission(tab.permission),
      ),
    [hasPermission],
  );

  const safeTabValue = tabValue < visibleTabs.length ? tabValue : 0;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const tab = visibleTabs[newValue];
    if (tab) {
      navigate(`${location.pathname}?tab=${tab.id}`, { replace: true });
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.get<DashboardData>(
          "/api/management/dashboard/statistics/",
        );
        setDashboardData(response.data);
      } catch (err: unknown) {
        const errorResponse = (
          err as { response?: { status?: number; data?: { detail?: string } } }
        )?.response;
        let errorMessage =
          "Failed to load dashboard statistics. Please try again.";
        if (errorResponse?.status === 401) {
          errorMessage = "Unauthorized: Please log in again.";
          dispatch(logout());
          navigate("/login");
        } else if (errorResponse?.status === 403) {
          errorMessage =
            "You do not have permission to access dashboard statistics.";
        } else if (errorResponse?.data?.detail) {
          errorMessage = errorResponse.data.detail;
        }
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [dispatch, navigate]);

  const renderTabContent = (tabId: string) => {
    switch (tabId) {
      case "admin":
        return dashboardData ? <AdminDashboard data={dashboardData} /> : null;
      case "my-dash":
        return <MyDashboardDashboard />;
      case "sales":
        return <SalesDashboard />;
      case "operations":
        return <OperationsDashboard />;
      case "streams":
        return <StreamsDashboard />;
      case "ceo":
        return <CeoDashboard />;
      case "contacts":
        return <ContactsDashboard />;
      case "venues":
        return <VenuesDashboard />;
      case "pilots":
        return <PixelPilotsDashboard />;
      case "user-login":
        return <UserLoginMonitor />;
      case "academy":
        return <AcademyDashboard />;
      case "chema":
        return <ChemaDashboard />;
      case "funeral":
        return <FuneralHomesDashboard />;
      case "insurance":
        return <InsuranceCompaniesDashboard />;
      case "balance":
        return <MyBalanceDashboard />;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Tabs
        value={safeTabValue}
        onChange={handleTabChange}
        aria-label="dashboard tabs"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {visibleTabs.map((tab, index) => (
          <Tab
            key={tab.id}
            label={
              tab.id === "user-login" ? (
                <NewFeatureBadge feature="user-login">{tab.label}</NewFeatureBadge>
              ) : (
                tab.label
              )
            }
            id={`dashboard-tab-${index}`}
          />
        ))}
      </Tabs>

      {isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {visibleTabs.map((tab, index) => (
        <TabPanel key={tab.id} value={safeTabValue} index={index}>
          {renderTabContent(tab.id)}
        </TabPanel>
      ))}
    </Box>
  );
};

export default MainDashboard;
